import { useState } from 'react';
import { X, Mail, Loader2, User, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: SupabaseUser | null;
  onSendMagicLink: (email: string) => Promise<{ error: string | null }>;
  onSignInWithGoogle: () => Promise<{ error: string | null }>;
  onSignInWithApple: () => Promise<{ error: string | null }>;
  onSignOut: () => Promise<void>;
}

export function AuthModal({
  isOpen,
  onClose,
  user,
  onSendMagicLink,
  onSignInWithGoogle,
  onSignInWithApple,
  onSignOut,
}: AuthModalProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      const result = await onSendMagicLink(email);

      if (result.error) {
        setError(result.error);
      } else {
        setSuccess(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsGoogleLoading(true);
    try {
      const result = await onSignInWithGoogle();
      if (result.error) {
        setError(result.error);
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setIsAppleLoading(true);
    try {
      const result = await onSignInWithApple();
      if (result.error) {
        setError(result.error);
      }
    } finally {
      setIsAppleLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      await onSignOut();
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-md" onClick={onClose} />

      {/* Modal */}
      <div
        className={cn(
          'relative w-full max-w-sm',
          'p-6',
          'rounded-2xl',
          'bg-card border border-muted',
          'animate-scale-in'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-medium text-main">{user ? 'Account' : 'Sign In'}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {user ? (
          // Logged in state
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-elevated">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                <User className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-main">{user.email}</p>
                <p className="text-xs text-muted">Syncing enabled</p>
              </div>
            </div>

            <button
              onClick={handleSignOut}
              disabled={isLoading}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-3',
                'rounded-lg border border-red-500/30',
                'text-red-400 hover:bg-red-500/10',
                'transition-colors',
                'disabled:opacity-50'
              )}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </>
              )}
            </button>
          </div>
        ) : success ? (
          // Success state - email sent
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
              <Mail className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <h3 className="text-main font-medium mb-1">Check your email</h3>
              <p className="text-sm text-secondary">
                We sent a magic link to <span className="text-main">{email}</span>
              </p>
            </div>
            <p className="text-xs text-muted">
              Click the link in the email to sign in. You can close this window.
            </p>
            <button
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
              className="text-sm text-primary-400 hover:underline"
            >
              Use a different email
            </button>
          </div>
        ) : showEmailForm ? (
          // Magic link form
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-secondary">
              Enter your email and we'll send you a magic link to sign in.
            </p>

            {/* Email */}
            <div>
              <label className="block text-sm text-secondary mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className={cn(
                    'w-full pl-10 pr-4 py-3',
                    'rounded-lg',
                    'bg-elevated border border-muted',
                    'text-main placeholder:text-muted',
                    'focus:outline-none focus:border-primary-600/50'
                  )}
                />
              </div>
            </div>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-400 bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                'w-full py-3 rounded-lg',
                'bg-primary-600 text-white font-medium',
                'hover:bg-primary-500 transition-colors',
                'disabled:opacity-50',
                'flex items-center justify-center gap-2'
              )}
            >
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Magic Link'}
            </button>

            <button
              type="button"
              onClick={() => setShowEmailForm(false)}
              className="w-full text-sm text-primary-400 hover:underline"
            >
              Back to sign in options
            </button>
          </form>
        ) : (
          // Sign in options
          <div className="space-y-4">
            <p className="text-sm text-secondary">Sign in to sync your data across devices.</p>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-400 bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>
            )}

            {/* Apple Sign In */}
            <button
              onClick={handleAppleSignIn}
              disabled={isAppleLoading}
              className={cn(
                'w-full py-3 rounded-lg',
                'bg-black text-white font-medium',
                'hover:bg-gray-900 transition-colors',
                'disabled:opacity-50',
                'flex items-center justify-center gap-3'
              )}
            >
              {isAppleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  Sign in with Apple
                </>
              )}
            </button>

            {/* Google Sign In */}
            <button
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              className={cn(
                'w-full py-3 rounded-lg',
                'bg-white text-gray-800 font-medium',
                'border border-gray-300',
                'hover:bg-gray-50 transition-colors',
                'disabled:opacity-50',
                'flex items-center justify-center gap-3'
              )}
            >
              {isGoogleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-muted" />
              <span className="text-xs text-muted">or</span>
              <div className="flex-1 h-px bg-muted" />
            </div>

            {/* Magic Link option */}
            <button
              onClick={() => setShowEmailForm(true)}
              className={cn(
                'w-full py-3 rounded-lg',
                'bg-elevated text-main font-medium',
                'border border-muted',
                'hover:bg-muted/50 transition-colors',
                'flex items-center justify-center gap-2'
              )}
            >
              <Mail className="w-5 h-5" />
              Continue with Email
            </button>

            <p className="text-xs text-center text-muted">
              No password needed. Sign in securely with Apple, Google, or a magic link.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
